/**
 * Shared validation utilities
 */

// Password validation constants - must match backend
export const PASSWORD_MIN_LENGTH = 12;

/**
 * Validate password meets requirements
 * @param password Password to validate
 * @returns Object with isValid boolean and optional error message
 */
export function validatePassword(password: string): { isValid: boolean; error?: string } {
  if (!password) {
    return { isValid: false, error: 'Password is required' };
  }
  if (password.length < PASSWORD_MIN_LENGTH) {
    return { isValid: false, error: `Password must be at least ${PASSWORD_MIN_LENGTH} characters` };
  }
  return { isValid: true };
}

/**
 * Get password hint text for display
 */
export function getPasswordHint(): string {
  return `Must be at least ${PASSWORD_MIN_LENGTH} characters`;
}

/**
 * Check if a string is a valid UUID format
 * @param value String to validate
 * @returns true if value is a valid UUID
 */
export function isValidUUID(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
}

/**
 * Characters that are never allowed in an email address.
 * Used for real-time input filtering.
 */
const INVALID_EMAIL_CHARS = /[<>()[\]\\,;:\s"]/g;

/**
 * Standard email validation pattern.
 * Validates format: local-part@domain.tld
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Filter out characters that are never allowed in email addresses.
 * Call this on input change to prevent invalid characters from being entered.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterEmailInput(value: string): string {
  return value.replace(INVALID_EMAIL_CHARS, '');
}

/**
 * Validate email format against standard patterns.
 * Call this on blur to check if the email is valid.
 * @param email Email to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateEmail(email: string): { isValid: boolean; errorKey?: string } {
  // Empty email is valid (field is optional)
  if (!email || email.trim() === '') {
    return { isValid: true };
  }

  // Check format against standard pattern
  if (!EMAIL_PATTERN.test(email)) {
    return { isValid: false, errorKey: 'common:validation.email' };
  }

  return { isValid: true };
}

// ============================================================================
// URL Validation
// ============================================================================

/**
 * Characters that should be filtered from URL input (control chars, some special chars).
 * More permissive than email since URLs can contain many special characters.
 */
const INVALID_URL_CHARS = /[\s<>"{}|\\^`]/g;

/**
 * URL validation pattern - checks for valid URL format.
 * Accepts http://, https://, or protocol-relative URLs.
 */
const URL_PATTERN = /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/i;

/**
 * Filter out characters that are never allowed in URLs.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterUrlInput(value: string): string {
  return value.replace(INVALID_URL_CHARS, '');
}

/**
 * Validate URL format.
 * @param url URL to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateUrl(url: string): { isValid: boolean; errorKey?: string } {
  // Empty URL is valid (field is optional)
  if (!url || url.trim() === '') {
    return { isValid: true };
  }

  // Check format against URL pattern
  if (!URL_PATTERN.test(url)) {
    return { isValid: false, errorKey: 'common:validation.url' };
  }

  return { isValid: true };
}

// ============================================================================
// Domain Validation
// ============================================================================

/**
 * Characters allowed in domain names (alphanumeric, hyphen, dot).
 */
const INVALID_DOMAIN_CHARS = /[^a-zA-Z0-9.-]/g;

/**
 * Domain validation pattern - validates domain format without protocol.
 * Examples: example.com, sub.example.co.uk
 */
const DOMAIN_PATTERN = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

/**
 * Filter input to only allow domain-valid characters.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterDomainInput(value: string): string {
  return value.toLowerCase().replace(INVALID_DOMAIN_CHARS, '');
}

/**
 * Validate domain format (without protocol).
 * @param domain Domain to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateDomain(domain: string): { isValid: boolean; errorKey?: string } {
  // Empty domain is valid (field is optional)
  if (!domain || domain.trim() === '') {
    return { isValid: true };
  }

  // Check format against domain pattern
  if (!DOMAIN_PATTERN.test(domain)) {
    return { isValid: false, errorKey: 'common:validation.domain' };
  }

  return { isValid: true };
}

// ============================================================================
// Phone Number Validation
// ============================================================================

/**
 * Characters allowed in phone numbers:
 * - Digits (0-9)
 * - Plus sign (+) at the beginning for international format
 * - Spaces, hyphens, and parentheses for formatting
 * - Letter 'x' or 'X' for extension
 */
const INVALID_PHONE_CHARS = /[^0-9+\-\s()xX]/g;

/**
 * Phone validation pattern.
 * Allows:
 * - Optional + at the start for international format
 * - Digits, spaces, hyphens, parentheses for formatting
 * - Optional extension with 'x' or 'ext' followed by digits
 * Minimum 7 digits required (shortest valid phone numbers)
 */
const PHONE_PATTERN = /^(\+)?[\d\s\-()]+(\s*(x|ext\.?)\s*\d+)?$/i;

/**
 * Filter input to only allow phone number characters.
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterPhoneInput(value: string): string {
  // Allow + only at the beginning
  const filtered = value.replace(INVALID_PHONE_CHARS, '');
  // Remove any + that isn't at the start
  const plusIndex = filtered.indexOf('+');
  if (plusIndex > 0) {
    return filtered.slice(0, plusIndex) + filtered.slice(plusIndex).replace(/\+/g, '');
  }
  return filtered;
}

/**
 * Validate phone number format.
 * @param phone Phone number to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validatePhone(phone: string): { isValid: boolean; errorKey?: string } {
  // Empty phone is valid (field is optional)
  if (!phone || phone.trim() === '') {
    return { isValid: true };
  }

  // Check format against phone pattern
  if (!PHONE_PATTERN.test(phone)) {
    return { isValid: false, errorKey: 'common:validation.phone' };
  }

  // Check minimum digit count (at least 7 digits for valid phone numbers)
  const digitCount = (phone.match(/\d/g) || []).length;
  if (digitCount < 7) {
    return { isValid: false, errorKey: 'common:validation.phoneTooShort' };
  }

  // Check maximum reasonable length (15 digits is E.164 max + some for extension)
  if (digitCount > 20) {
    return { isValid: false, errorKey: 'common:validation.phoneTooLong' };
  }

  return { isValid: true };
}

// ============================================================================
// HS Code Validation (Harmonized System code for international trade)
// ============================================================================

/**
 * Characters allowed in HS codes (digits and dots).
 */
const INVALID_HS_CODE_CHARS = /[^0-9.]/g;

/**
 * HS Code validation pattern.
 * Standard formats:
 * - 4 digits (chapter.heading): 8471
 * - 6 digits (subheading): 847130 or 8471.30
 * - 8-10 digits (national tariff lines): 84713000 or 8471.30.00
 */
const HS_CODE_PATTERN = /^(\d{4}|\d{4}\.\d{2}|\d{6}|\d{4}\.\d{2}\.\d{2}|\d{8}|\d{4}\.\d{2}\.\d{2}\.\d{2}|\d{10})$/;

/**
 * Filter input to only allow HS code characters (digits and dots).
 * @param value The input value to filter
 * @returns The filtered value with invalid characters removed
 */
export function filterHsCodeInput(value: string): string {
  return value.replace(INVALID_HS_CODE_CHARS, '');
}

/**
 * Validate HS code format.
 * @param hsCode HS code to validate
 * @returns Object with isValid boolean and optional error key for i18n
 */
export function validateHsCode(hsCode: string): { isValid: boolean; errorKey?: string } {
  // Empty HS code is valid (field is optional)
  if (!hsCode || hsCode.trim() === '') {
    return { isValid: true };
  }

  // Check format against HS code pattern
  if (!HS_CODE_PATTERN.test(hsCode)) {
    return { isValid: false, errorKey: 'common:validation.hsCode' };
  }

  return { isValid: true };
}
